import React from 'react';
import { useSelector } from 'react-redux';

import {
  getItem as kvGetItem,
  setItem as kvSetItem,
} from '../../../modules/KeyValueStorage';

import analytics from '../../../modules/analytics';

import {
  useOnce,
  useOnProfile,
  useResponsiveEffect,
  useRouteName,
} from '../../../hooks';
import useAsyncEffect from '../../../../../packages/hooks/src/useAsyncEffect';
import { selectors } from '../../../store/CurrentUser.model';

const THREE_DAYS = 1000 * 60 * 60 * 24 * 3;
const IS_DOT_DISMISSED_KEY = 'mobile-header-search-icon-dot-dismissed';
const DOT_FIRST_SEEN_KEY = 'mobile-header-search-icon-dot-first-seen';

const getDotDismissed = async () => kvGetItem(IS_DOT_DISMISSED_KEY);
const setDotDismissed = async () => kvSetItem(IS_DOT_DISMISSED_KEY, true);

const getDotExpired = async () => {
  const now = new Date().getTime();
  const firstSeenTime = await kvGetItem(DOT_FIRST_SEEN_KEY);

  if (!firstSeenTime) {
    await kvSetItem(DOT_FIRST_SEEN_KEY, now);
    return false;
  }

  return now - firstSeenTime > THREE_DAYS;
};

export default function useMobileHeaderSearchIcon() {
  const is = useResponsiveEffect();
  const isOnProfile = useOnProfile();
  const isLoggedIn = useSelector(selectors.isLoggedIn);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [routeName, _] = useRouteName();

  const [showDot, setShowDot] = React.useState<boolean>(false);
  const [dotStateFetched, setDotStateFetched] = React.useState<boolean>(false);

  const bookingFlowRoutes = ['provider_vanity.book', 'profile.book'];
  const isOnBookingFlow = bookingFlowRoutes.includes(routeName);
  const showMobileSearch = (!isLoggedIn
    && (isOnProfile || isOnBookingFlow)
    && (is.mobile || is.tablet));

  useAsyncEffect(async () => {
    // Only do the next few things
    // and meet all the criteria to see the search icon.
    if (showMobileSearch) {
      const isDotExpired = await getDotExpired();
      const isDotDismissed = await getDotDismissed();

      if (!isDotExpired && !isDotDismissed) {
        setShowDot(true);
      } else {
        setShowDot(false);
      }
      setDotStateFetched(true);
    }
  }, [showMobileSearch]);

  useOnce(() => {
    if (dotStateFetched) {
      analytics.track('client_mobile_header_search_btn_seen', {
        dot_shown: showDot,
      });
    }
  }, dotStateFetched || null);

  const dismissDotAndTrackSearchClick = React.useCallback(async () => {
    analytics.track('client_mobile_header_search_btn_clicked', {
      dot_shown: showDot,
    });

    await setDotDismissed();
    setShowDot(false);
  }, [showDot]);

  return {
    showDot,
    showMobileSearch,
    dismissDotAndTrackSearchClick,
  };
}
